import React, { useEffect } from 'react';
import { PrdEditorConfig, PrdDocument } from '../types';
import { usePrdStore } from '../store/prd-store';
import { EditorToolbar } from './EditorToolbar';
import { EditorSidebar } from './EditorSidebar';
import { EditorContent } from './EditorContent';
import { EditorPreview } from './EditorPreview';
import { AiAssistant } from './AiAssistant';
import { VersionHistory } from './VersionHistory';
import { Comments } from './Comments';

export interface PrdEditorProps extends Partial<PrdEditorConfig> {
  document?: PrdDocument;
  className?: string;
}

export const PrdEditor: React.FC<PrdEditorProps> = ({
  document,
  className = '',
  ...configProps
}) => {
  const {
    document: currentDocument,
    activeView,
    config,
    setConfig,
    loadDocument,
    createDocument,
  } = usePrdStore();

  // Initialize config
  useEffect(() => {
    setConfig(configProps);
  }, [configProps, setConfig]);

  // Load document
  useEffect(() => {
    if (document) {
      loadDocument(document);
    } else if (!currentDocument) {
      createDocument();
    }
  }, [document, currentDocument, loadDocument, createDocument]);

  if (!currentDocument) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`prd-editor flex flex-col h-full ${config.mode === 'dark' ? 'dark' : ''} ${className}`}>
      {/* Toolbar */}
      {config.showToolbar && (
        <EditorToolbar />
      )}

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {config.showOutline && (
          <EditorSidebar />
        )}

        {/* Editor/Preview */}
        <div className="flex-1 flex">
          {activeView === 'editor' || activeView === 'split' ? (
            <div className={`${activeView === 'split' ? 'w-1/2' : 'w-full'} border-r`}>
              <EditorContent />
            </div>
          ) : null}

          {activeView === 'preview' || activeView === 'split' ? (
            <div className={`${activeView === 'split' ? 'w-1/2' : 'w-full'}`}>
              <EditorPreview />
            </div>
          ) : null}
        </div>

        {/* Right Panels */}
        <div className="flex">
          {config.showComments && (
            <Comments />
          )}
          
          {config.showVersionHistory && (
            <VersionHistory />
          )}
          
          {config.showAiAssistant && config.aiConfig?.enabled && (
            <AiAssistant />
          )}
        </div>
      </div>
    </div>
  );
};